import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Check if user is logged in on mount
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get('/api/users/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setAuthError('Oturum süresi doldu, lütfen tekrar giriş yapın.');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setAuthError(null);
    try {
      const response = await axios.post('/api/users/login', {
        email,
        password
      });
      const { token, ...userData } = response.data;
      localStorage.setItem('token', token);
      setUser(userData);
      return userData;
    } catch (error) {
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        'Giriş yapılamadı, lütfen tekrar deneyin.';
      
      setAuthError(errorMessage);
      throw error;
    }
  };

  const register = async (username, email, password, invitationCode) => {
    setAuthError(null);
    
    try {
      const response = await axios.post('/api/users', {
        username,
        email,
        password,
        invitationCode
      });
      const { token, ...userData } = response.data;
      localStorage.setItem('token', token);
      setUser(userData);
      return userData;
    } catch (error) {
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        'Kayıt işlemi başarısız oldu, lütfen tekrar deneyin.';
      
      setAuthError(errorMessage);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setAuthError(null);
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  // Set up axios interceptor for authentication
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for handling expired tokens or auth errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && user) {
          // If we get an unauthorized error and we thought we were logged in,
          // the token is likely expired - log out
          logout();
          setAuthError('Oturum süresi doldu, lütfen tekrar giriş yapın.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    isSuperAdmin: !!user?.isSuperAdmin,
    loading,
    login,
    register,
    logout,
    authError,
    clearAuthError,
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="animate-pulse text-white text-xl">Yükleniyor...</div>
    </div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 